import React, { useContext, useEffect, Suspense, lazy } from 'react';
import { Router, RouteComponentProps, LocationProvider, navigate } from '@reach/router';
import { createRoot } from 'react-dom/client';
import { LinearProgress } from '@mui/material';
import './index.css';
import reportWebVitals from './reportWebVitals';
import ErrorBoundary from './pages/errorBoundary';
import { StyledEngineProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file

// Route-level code splitting. Each page becomes its own chunk so the initial
// bundle no longer carries every admin + super-admin page eagerly. Components
// used as wrappers/layouts (Admin, SuperAdmin, ErrorBoundary, ApplicationContext)
// stay eager because they render around every route.
const AdminTemplates = lazy(() => import('./pages/admin/templates'));
const AdminDashboard = lazy(() => import('./pages/admin/dashboard'));
const AdminAccount = lazy(() => import('./pages/admin/account'));
const AdminTeam = lazy(() => import('./pages/admin/team'));
const AdminSignIn = lazy(() => import('./pages/admin/login'));
const AdminSso = lazy(() => import('./pages/admin/sso'));

const SANegotiatorDashboard = lazy(() => import('./pages/super-admin/dashboard-negotiator'));
const SACoreDashboard = lazy(() => import('./pages/super-admin/dashboard-core'));
const SACoreDetails = lazy(() => import('./pages/super-admin/dashboard-core/core-details'));
const SALogin = lazy(() => import('./pages/super-admin/login'));
const SALocations = lazy(() => import('./pages/super-admin/billing-accounts'));
const SALocationTokens = lazy(() => import('./pages/super-admin/billing-accounts/location-tokens'));
const SABillsPage = lazy(() => import('./pages/super-admin/bills'));
const SAMarkupMargin = lazy(() => import('./pages/super-admin/markup-margin'));
const SALookups = lazy(() => import('./pages/super-admin/lookups'));
const SAAiModels = lazy(() => import('./pages/super-admin/ai-models'));
const SARoles = lazy(() => import('./pages/super-admin/roles'));
const SAAnalytics = lazy(() => import('./pages/super-admin/analytics'));
const SASettings = lazy(() => import('./pages/super-admin/settings'));
const SAApiKeys = lazy(() => import('./pages/super-admin/settings/apis'));
const SACustomValues = lazy(() => import('./pages/super-admin/custom-values'));
const SAUnifiedUsers = lazy(() => import('./pages/super-admin/unified-users'));
const SAReportsArtifacts = lazy(() => import('./pages/super-admin/artifacts/ArtifactsList'));
const SACreateArtifact = lazy(() => import('./pages/super-admin/artifacts/create'));
const SAZazuAuditTrails = lazy(() => import('./pages/super-admin/zazu-audit-trails'));
const SAMyProfile = lazy(() => import('./pages/super-admin/settings/my-profile'));
const SAJobs = lazy(() => import('./pages/super-admin/jobs'));
const SADiagnostics = lazy(() => import('./pages/super-admin/extension-health/diagnostics'));
const SASupportTickets = lazy(() => import('./pages/super-admin/extension-health/support-tickets'));
const SADiagnosticsLookup = lazy(() => import('./pages/super-admin/extension-health/lookup'));
const SAOAuthGhl = lazy(() => import('./pages/super-admin/oAuth/ghl'));
const SAApiDocs = lazy(() => import('./pages/super-admin/api-docs'));

const BillingDetails = lazy(() => import('./organisms/billingDetails'));
const Errors404 = lazy(() => import('./pages/errors/404'));
const Expired = lazy(() => import('./pages/errors/expired'));

import { ApplicationContext, ApplicationState, TableState } from './particles/context';
import { Admin, SuperAdmin } from './templates';
import { ThemeWrapper, isStaffRole, routes } from '@particles';
import { callGetUser } from './particles/api/user';
import { signOutEverywhere } from './firebase/session';

/** The dashboard's sign-in pages, open without a session: login, code exchange, and the legacy extension hand-off. */
const isAdminSignInPath = (pathname: string) =>
  /^\/admin\/(login|sso)\/?$/.test(pathname) || /^\/admin\/[^/]+\/[^/]+\/?$/.test(pathname);

export interface AppWrapperProps extends RouteComponentProps {
  /** The page contents */
  children?: React.ReactElement | React.ReactElement[];
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 5 minutes — fresh data still refetches on tab focus, but only after
      // it has actually gone stale, so a quick tab-switch no longer re-runs
      // every heavy analytics/billing query. Per-query overrides remain
      // available (e.g. useArtifacts uses staleTime: 0 for live job status).
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true
    }
  }
});

const AppWrapper: React.FC<AppWrapperProps> = (props: AppWrapperProps) => {
  const { children, location } = props;
  const pathname = location?.pathname ?? '';

  const {
    state: { user },
    dispatch
  } = useContext(ApplicationContext);

  const userEmail = user?.email ?? '';
  const userRoleName = user?.role?.name;

  // Re-run only when the path or signed-in identity changes. Previously this
  // depended on `props` itself, which @reach/router replaces on every
  // re-render, so callGetUser ran on every render.
  useEffect(() => {
    if (pathname.startsWith('/admin')) {
      document.title = 'SalesRook Admin Portal';
      // Signed out: the dashboard's own sign-in, which tries the extension first.
      if (!user && !isAdminSignInPath(pathname)) navigate('/admin/login');
    } else {
      document.title = 'SalesRook SuperAdmin Portal';
      if (!user) {
        if (!pathname.includes('/login')) {
          navigate('/login');
        }
      }
    }

    if (user) {
      callGetUser({ email: userEmail }).then((value) => {
        if (value.code === 200) {
          dispatch({ key: 'user', value: value.data, type: 'set' });
        } else if (value.code === 403) {
          signOutEverywhere();
          if (isStaffRole(userRoleName)) {
            navigate('/login');
          } else if (userRoleName === 'Admin') {
            navigate('/admin/login');
          } else {
            navigate('/expired');
          }
          dispatch({ key: 'user', value: undefined, type: 'set' });
        }
      }).catch(() => {
        // Server down or network error — clear stale session to prevent stuck loop
        signOutEverywhere();
        dispatch({ key: 'user', value: undefined, type: 'set' });
        navigate('/login');
      });
    }
  }, [pathname, userEmail]);

  return (
    <ErrorBoundary>
      <StyledEngineProvider injectFirst>{children}</StyledEngineProvider>
    </ErrorBoundary>
  );
};

const AdminLogin: React.FC<RouteComponentProps> = (props: RouteComponentProps) => {
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    const { email, token } = props;
    if (email && token) {
      // Drop any other Firebase session in this browser first, or its token —
      // not this one — would go out with every request.
      signOutEverywhere().then(() => {
        localStorage.setItem('token', token);
        localStorage.setItem('email', email);
        return callGetUser({ email });
      }).then((value) => {
        if (value.code === 200) {
          dispatch({ key: 'user', value: value.data, type: 'set' });
          localStorage.setItem('user', JSON.stringify(value.data));

          setTimeout(() => {
            navigate('/admin');
          }, 2000);
        }
      });
    }
  }, [props]);

  const { dispatch } = useContext(ApplicationContext);

  return <div>Signing ... </div>;
};

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <LocationProvider>
      <ApplicationState>
        <TableState>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <ThemeWrapper>
              <Suspense fallback={<LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />}>
              <Router basepath="/">
                <AppWrapper path="/">
                  <AdminSignIn path="/admin/login" />
                  <AdminSso path="/admin/sso" />
                  {/* Legacy: extension builds that put their token in the URL. Kept until those are gone. */}
                  <AdminLogin path="/admin/:token/:email" />
                  <Admin path="/admin">
                    <AdminDashboard path="/" />
                    <AdminAccount path="/account" />
                    <AdminTeam path="/team" />
                    <AdminTemplates path="/templates" />
                    <BillingDetails type="admin" path="/billing" />
                    <Errors404 default />
                  </Admin>
                  <SuperAdmin path="/">
                    <SANegotiatorDashboard path={`${routes.SA_Negotiator}`} />
                    <SACoreDashboard path={`${routes.SA_Core}`} />
                    <SAUnifiedUsers path={`${routes.SA_Unified_Users}`} />
                    <SAReportsArtifacts path={`${routes.SA_Artifacts}`} />
                    <SACreateArtifact path={`${routes.SA_Artifacts}/create`} />
                    <SACoreDetails path={`${routes.SA_Core}/:locationId`} />
                    <SALocations path={`${routes.SA_BillingAccounts}`} />
                    <SABillsPage path={`${routes.SA_Bills}`} />
                    <SAMarkupMargin path={`${routes.SA_MarkupMargins}`} />
                    <SALookups path={`${routes.SA_Lookups}`} />
                    <SAAiModels path={`${routes.SA_AiModels}`} />
                    <SARoles path={`${routes.SA_Roles}`} />
                    <SAAnalytics path={`${routes.SA_Analytics}`} />
                    <SAApiKeys path={`${routes.SA_ApiKeys}`} />
                    <SASettings path={`${routes.SA_Settings}`} />
                    <SAMyProfile path={`${routes.SA_MyProfile}`} />
                    <BillingDetails type="superadmin" path={`${routes.SA_NegotiatorDetails}/:id`} />
                    <SALocationTokens
                      path={`${routes.SA_BillingTokens}/:billingAccountId`}
                      type={'superadmin'}
                    />
                    <SACustomValues path={`${routes.SA_CustomValues}`} />
                    <SAZazuAuditTrails path={`${routes.SA_ZazuAuditTrails}`} />
                    <SAJobs path={`${routes.SA_Jobs}`} />
                    <SADiagnostics path={`${routes.SA_Diagnostics}`} />
                    <SASupportTickets path={`${routes.SA_SupportTickets}`} />
                    <SADiagnosticsLookup path={`${routes.SA_DiagnosticsLookup}`} />
                    <SAApiDocs path={`${routes.SA_ApiDocs}`} />
                    <Errors404 default />
                  </SuperAdmin>

                  <SALogin path="/login" />
                  <Errors404 path="/404" />
                  <Expired path="/expired" />
                </AppWrapper>
                <SAOAuthGhl path={`${routes.SA_OAuthGhl}`} />
                <SALocationTokens path="/location-tokens/:locationId" type={'superadmin'} />
              </Router>
              </Suspense>
              <ToastContainer />
            </ThemeWrapper>
          </LocalizationProvider>
        </TableState>
      </ApplicationState>
    </LocationProvider>
  </QueryClientProvider>
);

export default App;

const root = document.getElementById('root');
if (root) {
  createRoot(root).render(<App />);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
